import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";

export interface Event {
  id: string;
  title: string;
  description: string | null;
  event_date: string;
  timezone: string | null;
  registration_link: string | null;
  is_active: boolean;
  show_on_news: boolean;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface EventAttachment {
  id: string;
  event_id: string;
  attachment_type: string;
  url: string;
  file_name: string | null;
  title: string | null;
  thumbnail_url: string | null;
  is_featured: boolean;
  created_at: string;
}

export interface EventAudience {
  id: string;
  event_id: string;
  role: string;
  created_at: string;
}

export interface EventWithDetails extends Event {
  attachments: EventAttachment[];
  audiences: EventAudience[];
}

export function useEvents() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const { data: events, isLoading } = useQuery({
    queryKey: ["events"],
    queryFn: async () => {
      const { data: eventsData, error } = await supabase
        .from("events")
        .select("*")
        .order("event_date", { ascending: true });

      if (error) throw error;

      // Fetch attachments and audiences for all events
      const eventIds = eventsData?.map((e) => e.id) || [];
      
      let attachments: EventAttachment[] = [];
      let audiences: EventAudience[] = [];

      if (eventIds.length > 0) {
        const [attachmentsResult, audiencesResult] = await Promise.all([
          supabase.from("event_attachments").select("*").in("event_id", eventIds),
          supabase.from("event_audiences").select("*").in("event_id", eventIds),
        ]);

        attachments = (attachmentsResult.data as EventAttachment[]) || [];
        audiences = (audiencesResult.data as EventAudience[]) || [];
      }

      return eventsData?.map((event) => ({
        ...event,
        attachments: attachments.filter((a) => a.event_id === event.id),
        audiences: audiences.filter((a) => a.event_id === event.id),
      })) as EventWithDetails[];
    },
  });

  const createEvent = useMutation({
    mutationFn: async (data: {
      title: string;
      description?: string;
      event_date: string;
      timezone?: string;
      registration_link?: string;
      is_active?: boolean;
      show_on_news?: boolean;
      audiences?: string[];
      attachments?: { type: string; url: string; file_name?: string; title?: string; is_featured?: boolean }[];
    }) => {
      // Create event
      const { data: event, error: eventError } = await supabase
        .from("events")
        .insert({
          title: data.title,
          description: data.description || null,
          event_date: data.event_date,
          timezone: data.timezone || null,
          registration_link: data.registration_link || null,
          is_active: data.is_active ?? true,
          show_on_news: data.show_on_news ?? false,
          created_by: user?.id,
        })
        .select()
        .single();

      if (eventError) throw eventError;

      // Add audiences
      if (data.audiences && data.audiences.length > 0) {
        const { error: audiencesError } = await supabase
          .from("event_audiences")
          .insert(data.audiences.map((role) => ({
            event_id: event.id,
            role,
          })));

        if (audiencesError) throw audiencesError;
      }

      // Add attachments
      if (data.attachments && data.attachments.length > 0) {
        const { error: attachmentsError } = await supabase
          .from("event_attachments")
          .insert(data.attachments.map((att) => ({
            event_id: event.id,
            attachment_type: att.type,
            url: att.url,
            file_name: att.file_name || null,
            title: att.title || null,
            is_featured: att.is_featured || false,
          })));

        if (attachmentsError) throw attachmentsError;
      }

      return event;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["events"] });
      queryClient.invalidateQueries({ queryKey: ["public_events"] });
    },
  });

  const updateEvent = useMutation({
    mutationFn: async (data: {
      id: string;
      title?: string;
      description?: string;
      event_date?: string;
      timezone?: string;
      registration_link?: string;
      is_active?: boolean;
      show_on_news?: boolean;
      audiences?: string[];
      attachments?: { type: string; url: string; file_name?: string; title?: string; is_featured?: boolean }[];
    }) => {
      const { id, audiences, attachments, ...eventData } = data;

      // Update event
      const { error: eventError } = await supabase
        .from("events")
        .update(eventData)
        .eq("id", id);

      if (eventError) throw eventError;

      // Update audiences if provided
      if (audiences !== undefined) {
        // Delete existing audiences
        await supabase.from("event_audiences").delete().eq("event_id", id);
        
        // Add new audiences
        if (audiences.length > 0) {
          const { error: audiencesError } = await supabase
            .from("event_audiences")
            .insert(audiences.map((role) => ({
              event_id: id,
              role,
            })));

          if (audiencesError) throw audiencesError;
        }
      }

      // Update attachments if provided
      if (attachments !== undefined) {
        // Delete existing attachments
        await supabase.from("event_attachments").delete().eq("event_id", id);
        
        // Add new attachments
        if (attachments.length > 0) {
          const { error: attachmentsError } = await supabase
            .from("event_attachments")
            .insert(attachments.map((att) => ({
              event_id: id,
              attachment_type: att.type,
              url: att.url,
              file_name: att.file_name || null,
              title: att.title || null,
              is_featured: att.is_featured || false,
            })));

          if (attachmentsError) throw attachmentsError;
        }
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["events"] });
      queryClient.invalidateQueries({ queryKey: ["public_events"] });
    },
  });

  const deleteEvent = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("events").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["events"] });
      queryClient.invalidateQueries({ queryKey: ["public_events"] });
    },
  });

  return {
    events,
    isLoading,
    createEvent,
    updateEvent,
    deleteEvent,
  };
}

// Hook for public events (shown on News page)
export function usePublicEvents() {
  return useQuery({
    queryKey: ["public_events"],
    queryFn: async () => {
      const { data: eventsData, error } = await supabase
        .from("events")
        .select("*")
        .eq("is_active", true)
        .eq("show_on_news", true)
        .order("event_date", { ascending: true });

      if (error) throw error;

      const eventIds = eventsData?.map((e) => e.id) || [];
      
      let attachments: EventAttachment[] = [];

      if (eventIds.length > 0) {
        const { data } = await supabase
          .from("event_attachments")
          .select("*")
          .in("event_id", eventIds);

        attachments = (data as EventAttachment[]) || [];
      }

      return eventsData?.map((event) => ({
        ...event,
        attachments: attachments.filter((a) => a.event_id === event.id),
      })) as (Event & { attachments: EventAttachment[] })[];
    },
  });
}

// Hook for dashboard events (filtered by user role)
export function useDashboardEvents() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["dashboard_events", user?.id],
    queryFn: async () => {
      if (!user?.id) return [];

      // Fetch user's profile role
      const { data: profileData } = await supabase
        .from("profiles")
        .select("role")
        .eq("id", user.id)
        .single();

      const profileRole = profileData?.role;

      // Fetch user's roles from user_roles table
      const { data: userRolesData } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", user.id);

      const userRoles = (userRolesData || []).map((r) => r.role);

      // Check if user is admin
      const isAdmin = userRoles.includes("admin");

      const { data: eventsData, error } = await supabase
        .from("events")
        .select("*")
        .eq("is_active", true)
        .order("event_date", { ascending: true });

      if (error) throw error;

      const eventIds = eventsData?.map((e) => e.id) || [];
      
      let attachments: EventAttachment[] = [];
      let audiences: EventAudience[] = [];

      if (eventIds.length > 0) {
        const [attachmentsResult, audiencesResult] = await Promise.all([
          supabase.from("event_attachments").select("*").in("event_id", eventIds),
          supabase.from("event_audiences").select("*").in("event_id", eventIds),
        ]);

        attachments = (attachmentsResult.data as EventAttachment[]) || [];
        audiences = (audiencesResult.data as EventAudience[]) || [];
      }

      const eventsWithDetails = eventsData?.map((event) => ({
        ...event,
        attachments: attachments.filter((a) => a.event_id === event.id),
        audiences: audiences.filter((a) => a.event_id === event.id),
      })) as (Event & { attachments: EventAttachment[]; audiences: EventAudience[] })[];

      // Admins see all events
      if (isAdmin) {
        return eventsWithDetails;
      }

      // Filter events based on audience targeting
      return eventsWithDetails.filter((event) => {
        // If no audiences specified, show to everyone
        if (!event.audiences || event.audiences.length === 0) return true;

        const audienceRoles = event.audiences.map((a) => a.role);

        // Check profile role (entrepreneur, mentor)
        if (profileRole === "entrepreneur" && audienceRoles.includes("entrepreneur")) return true;
        if (profileRole === "mentor" && audienceRoles.includes("mentor")) return true;

        // Check user_roles (admin, instructor)
        if (audienceRoles.includes("admin") && userRoles.includes("admin")) return true;
        if (audienceRoles.includes("instructor") && userRoles.includes("instructor")) return true;

        return false;
      });
    },
    enabled: !!user,
  });
}
